import React, { useEffect, useState } from 'react';

import { CarouselNavigationContext } from '../InteractiveContent';
import { EmbedContext, GatingContext, WidgetsContext } from './../core/context';
import Content from '../core/content';
import { useDispatch } from 'react-redux';
import { updateHyperLinkSlide } from '../../../actions/spatial';
import get from 'lodash/get';
import has from 'lodash/has';


export const Actions = React.createContext({

  onBeforeExecution: (widget, action) => {},
  onAfterExecution: (widget, action) => {}
});

export function useActionExectioner() {
  const context = React.useContext(Actions);
  const interactiveContext = React.useContext(CarouselNavigationContext);
  const widgetContext = React.useContext(WidgetsContext);
  const contentContext = React.useContext(Content.Context);
  const gatingContext = React.useContext(GatingContext);
  const embedContext = React.useContext(EmbedContext);

  const dispatch = useDispatch();

  const [state, setState] = useState({
    execute: (widget, action) => {}
  });

  useEffect(() => {
    setState({
      execute: handleExecution
    });

  }, [interactiveContext, widgetContext]);

  const handleBeforeExecution = (widget, action) => {
    if(context.onBeforeExecution){
      context.onBeforeExecution(widget, action);
    }
  }

  const handleAfterExecution = (widget, action) => {
    if(context.onAfterExecution){
      context.onAfterExecution(widget, action);
    }
  }

  const handleExecution = (widget, action) => {
    const isPreview = get(widgetContext, 'preview', false);
    if(!action){
      return false;
    }

    handleBeforeExecution(widget, action);
    const type = action.type;
    const value = action.value;

    switch(type) {
      case 'link': {
        return handleLinkAction(widget, action, embedContext);
      }

      case 'pageAnchor': {
        return handlePageAnchorAction(interactiveContext, widget, action, widgetContext, embedContext);
      }

      case 'widgetAnchor': {
        return handleWidgetAnchorAction(interactiveContext, action, widgetContext);
      }

      case 'asset': {
        return handleAssetAction(widget, action, contentContext);
      }

      case 'page': {
        if(isPreview) {
          return handlePageActionPreview(widget, action, widgetContext);
        }
        return handlePageAction(widget, action, widgetContext);
      }

      case 'slide': {
        return handleSlideAction(widget, action, dispatch);
      }

      case 'carousel': {
        return handleCarouselAction(action, widgetContext, interactiveContext);
      }

      case 'gating': {
        return handleGatingAction(gatingContext, action, widgetContext);
      }
    }

    handleAfterExecution(widget, action);

    return false;
  }

  return state;
}

const handleLinkAction = (widget, action, embedContext) => {
  if(!action){
    return false;
  }

  const {newTab, value} = action;
  const currentWindow = window.self || window;


  if(embedContext.embedded && !newTab) {
    var isInIframe = window.parent !== window;
    if (isInIframe) {
      const parentWindow = window.parent;
      parentWindow.postMessage({
        type: "cleverstory:embed:link",
        payload: {
          action: action
        }
      }, '*');
    }
    return;
  }

  if(newTab){
    currentWindow.open(value);
  }else{
    currentWindow.location = value;
  }
}

const handlePageAnchorAction = (interactiveContext, widget, action, widgetContext, embedContext) => {
  if(!action || !interactiveContext){
    return false;
  }

  const {value} = action;

  const storyboardType = get(widgetContext, 'storyboard.type', '');

  if(embedContext.embedded && storyboardType !== 'INTERACTIVE_CONTENT') {
    var isInIframe = window.parent !== window;
    if (isInIframe) {
      const parentWindow = window.parent;
      parentWindow.postMessage({
        type: "cleverstory:embed:pageAnchor",
        payload: {
          action: action
        }
      }, '*');
    }
    return;
  }

  if (storyboardType === 'INTERACTIVE_CONTENT') {
    if (value || !has(action, 'value')) {
      interactiveContext.scrollToTop();
    } else {
      interactiveContext.scrollToBottom();
    }
  }

  if (storyboardType !== 'INTERACTIVE_CONTENT') {
    const shouldScrollToTop = value || !has(action, 'value');
    const isPreview = get(widgetContext, 'preview', false);

    const scrollIframe = (position) => {
      const parentDocument = window.parent.document;
      const iframe = parentDocument.getElementById('cs-preview-iframe');
      if (iframe) {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDocument) {
          iframeDocument.body.scrollTo({
            top: position,
            behavior: 'smooth'
          });
        }
      }
    };

    if (isPreview) {
      shouldScrollToTop ? scrollIframe(0) : scrollIframe(document.body.scrollHeight);
    } else {
      window.scrollTo(0, shouldScrollToTop ? 0 : document.body.scrollHeight);
    }
  }
}

const handleWidgetAnchorAction = (interactiveContext, action, widgetContext) => {
  if (!action) {
    return false;
  }

  const storyboardType = get(widgetContext, 'storyboard.type', '');

  if (storyboardType === 'INTERACTIVE_CONTENT') {
    interactiveContext.scrollToWidget(action);
  } else {
    const { value } = action;
    var widgetElement = document.getElementById(value);
    widgetElement.scrollIntoView();
  }
}

const handlePageAction = (widget, action, widgetContext) => {
  if (!action) {
    return false;
  }

  const { newTab } = action;
  const domain = get(widgetContext, 'domain', null);
  const alias = get(action, 'value.canonical', null);

  const value = domain + alias;

  const currentWindow = window.self || window;

  if (newTab) {
    currentWindow.open(value);
  } else {
    currentWindow.location = value;
  }
}

const handlePageActionPreview =(widget, action, widgetContext)=> {
  
  if (!action) {
    return false;
  }

  const parts = window.location.pathname.split("/");
  const pagesIndex = parts.indexOf("pages");
  const baseUrl = parts.slice(0, pagesIndex).join("/");
  const alias = get(action, 'value.id', null);
  const newPath = `${baseUrl}/pages/${alias}/preview`;
  window.top.location.href = newPath;
};

const handleSlideAction = (widget, action, dispatch) => {
  if (!action) {
    return false;
  }

  const { slide, page, uid } = action?.value || {};

  dispatch(updateHyperLinkSlide({
    slide,
    page,
    uid
  }));
}

const handleCarouselAction = (action, widgetContext, interactiveContext) => {
  const storyboardType = get(widgetContext, 'storyboard.type', '');
  const slideElement = document.querySelector(`[data-widget-id='${action?.value?.slideUid}']`);

  if (!action && !slideElement) {
    return false;
  }


  if (storyboardType === 'INTERACTIVE_CONTENT') {
    interactiveContext.scrollToWidget({
      ...action,
      value: action?.value?.carouselUid || "",
    });
  }

  widgetContext.updateCarouselSlide(action?.value || null);
}

const handleAssetAction = (widget, action, contentContext) => {

  if(!action){
    return false;
  }

  contentContext.viewContent(widget, action.value);
}

const handleGatingAction = (gatingContext, action, widgetContext) => {

  if (!action) {
    return false;
  }

  const {value} = action;

  if(value.gating != null) {

    const gatingConfig = {
      optional: value.optional,
      target: null,
      enabled: true,
      entityType: "gating_configuration",
      gating: {
        id: value.gating
      },
      trigger: {
        type: 'BUTTON_GATING',
        value: null
      },
      name: "Button Gating",
    };

    widgetContext.fetchGating(value.gating);
    gatingContext.triggerGating('BUTTON_GATING', null, gatingConfig);
  }
}
